<template>
  <div class="min-h-screen bg-slate-50 px-4 py-8 font-sans">
    <div class="mx-auto w-full max-w-md">
      <h1 class="text-center text-xl font-bold text-[#0d4169] mb-5">
        Order Summary
      </h1>

      <div v-if="loading" class="flex justify-center py-16">
        <div
          class="h-10 w-10 border-4 border-[#0d4169]/25 border-t-[#0d4169] rounded-full animate-spin"
          aria-label="Loading"
        ></div>
      </div>

      <div
        v-else-if="errorMessage"
        class="text-center text-sm text-red-600 bg-white border border-red-100 rounded-lg shadow-sm px-4 py-6"
      >
        {{ errorMessage }}
      </div>

      <div
        v-else-if="!billCard"
        class="text-center text-sm text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm px-4 py-6"
      >
        No unpaid bill found for this link.
      </div>

      <div v-else class="flex justify-center">
        <!-- Same card chrome as POS My Orders (one centered card, no QR / Edit / Scan) -->
        <div
          class="w-full max-w-[22rem] border border-gray-200 rounded-lg bg-white shadow-lg overflow-hidden flex flex-col"
        >
          <div
            class="bg-[#0d4169] text-white flex flex-wrap items-center justify-between gap-1 px-3 py-2 text-[11px]"
          >
            <span class="font-bold text-white">
              Table: {{ tableLabel }}
            </span>
            <span class="font-semibold text-white">
              O. No: {{ billCard.dailyOrderNo ?? '---' }}
            </span>
            <span class="text-white">{{ formatCardDate(billCard.createdAt) }}</span>
          </div>

          <div class="p-3 flex flex-col flex-1">
            <div
              class="grid grid-cols-[minmax(0,1fr)_2.25rem_3.25rem_3.25rem_0.875rem] gap-x-1 items-center justify-items-stretch text-[11px] text-gray-700"
            >
              <span class="text-left font-bold">Name</span>
              <span class="text-right font-bold">Qty</span>
              <span class="text-right font-bold">U.P</span>
              <span class="text-right font-bold">T.P</span>
              <span></span>
            </div>

            <div class="space-y-0.5">
              <div
                v-for="(item, idx) in cardItems"
                :key="item.localId || idx"
                class="grid grid-cols-[minmax(0,1fr)_2.25rem_3.25rem_3.25rem_0.875rem] gap-x-1 items-center justify-items-stretch py-0.5 font-bold text-[11px] text-gray-700 border-b border-gray-100 last:border-b-0"
              >
                <span class="bill-card-item-name">{{ item.name }}</span>
                <span class="text-right tabular-nums">{{ item.qty }}</span>
                <span class="text-right tabular-nums">
                  {{ Number(item.unitPrice || 0).toFixed(1) }}
                </span>
                <span class="text-right tabular-nums">
                  {{ (Number(item.qty || 0) * Number(item.unitPrice || 0)).toFixed(1) }}
                </span>
                <span></span>
              </div>
            </div>

            <div v-if="cardItems.length === 0" class="text-gray-500 text-sm italic py-2">
              No items.
            </div>

            <div class="mt-2 pt-2 border-t border-gray-100 text-right text-[11px]">
              <span class="font-semibold text-gray-700">
                Total Amt: {{ totalAmt }}
              </span>
            </div>

            <button
              v-if="cardItems.length > 0"
              type="button"
              class="mt-3 w-full px-3 py-2 text-sm font-semibold text-white bg-green-600 rounded-md shadow-sm"
            >
              Agree and Pay
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { API_BASE_URL } from '../utils/api'

const route = useRoute()

const loading = ref(true)
const errorMessage = ref('')
const lineItems = ref([])

const orderGroupId = computed(() => {
  const raw =
    route.query.order_group_id ??
    route.query.orderGroupId ??
    ''
  return String(raw).trim()
})

const billCard = computed(() => {
  if (!Array.isArray(lineItems.value) || lineItems.value.length === 0) return null
  const first = lineItems.value[0]
  return {
    id: first.orderGroupId || orderGroupId.value,
    createdAt: first.createdAt,
    table: first.tableNumber,
    dailyOrderNo: first.dailyOrderNo,
    items: lineItems.value,
  }
})

const tableLabel = computed(() => {
  const table = billCard.value?.table
  if (table == null || table === '') return '---'
  const s = String(table)
  return s.length <= 5 ? s : '---'
})

const cardItems = computed(() => {
  const order = billCard.value
  if (!order || !Array.isArray(order.items)) return []
  const displayedItems = []
  order.items.forEach((i) => {
    const iMenuId = String(i.menuId ?? i.menu?.id ?? '')
    const excludeKey = String(i.excludeIngredients || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .join(',')
    const noteKey = String(i.note || '').trim()
    const existing = displayedItems.find(
      (mi) =>
        String(mi.menuId ?? '') === iMenuId &&
        mi.excludeKey === excludeKey &&
        mi.noteKey === noteKey
    )
    const qtyToAdd = Number(i.quantity ?? i.qty ?? 0) || 0
    const unitPrice =
      Number(i.unit_price ?? i.unitPrice ?? i.menu?.totalPrice ?? i.menu?.total_price ?? 0) || 0
    if (existing) {
      existing.qty += qtyToAdd
    } else {
      displayedItems.push({
        localId: `bill-${order.id}-${i.id || Math.random()}`,
        menuId: iMenuId,
        name: i.name || i.menu?.name || 'Item',
        qty: qtyToAdd,
        unitPrice,
        excludeKey,
        noteKey,
      })
    }
  })
  return displayedItems
})

const totalAmt = computed(() => {
  const order = billCard.value
  if (!Array.isArray(order?.items)) return '0.00'
  let grandTotal = 0
  order.items.forEach((i) => {
    const qty = Number(i.quantity ?? i.qty ?? 0) || 0
    grandTotal += (Number(i.unit_price ?? i.unitPrice ?? 0) || 0) * qty
  })
  return grandTotal.toFixed(2)
})

function formatCardDate(createdAt) {
  if (!createdAt) return '---'
  const datePart = String(createdAt).split('T')[0]
  const timePart = new Date(createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${datePart} ${timePart}`
}

async function fetchBill() {
  loading.value = true
  errorMessage.value = ''
  lineItems.value = []

  const gid = orderGroupId.value
  if (!gid) {
    errorMessage.value = 'Missing order_group_id in the link.'
    loading.value = false
    return
  }

  try {
    // Direct fetch — public guest page must not go through auth/session apiFetch side-effects
    const res = await fetch(
      `${API_BASE_URL}/orders/public/by-group/${encodeURIComponent(gid)}`
    )
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.message || `Failed to load bill (${res.status})`)
    }
    const data = await res.json()
    lineItems.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Failed to fetch bill detail:', err)
    errorMessage.value = err?.message || 'Failed to load bill. Please try again.'
  } finally {
    loading.value = false
  }
}

onMounted(fetchBill)
watch(orderGroupId, fetchBill)
</script>

<style scoped>
.bill-card-item-name {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
</style>
